// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { CampaignLink } from "@/components/campaigns/CampaignLink";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";
import type { Campaign } from "@/hooks/api/use-campaigns";
import type { ExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { DEFAULT_PRIMARY_COLUMN_WIDTHS } from "@/lib/column-defaults";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import { createCustomFieldColumns } from "@/pages/accounts/account-list/custom-field-column-utils";
import {
  METRIC_CONFIG,
  METRIC_DATA_TYPES,
} from "@/pages/campaigns/campaign-explorer/campaign-metrics-config";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type {
  CampaignWithFormulaColumns,
  FormulaTableColumn,
} from "@/types/formula-column";
import type { UnifiedTableColumn } from "@/types/table-column";

/** Campaign column with metric category information */
export interface CampaignColumn extends UnifiedTableColumn<Campaign> {
  category?: string;
  categoryStyle?: string;
}

export const getCampaignColumns = (
  filterOptions?: { type?: string[]; source_record_type?: string[] },
  connections?: ExternalConnectionLinks[],
  isLoadingConnections?: boolean,
  selectedMetrics?: Array<keyof CampaignMetrics>,
  formulaColumns?: FormulaTableColumn[],
  customFields?: CustomFieldDefinition[],
  onMetricClick?: (campaignId: string, metric: keyof CampaignMetrics) => void,
): CampaignColumn[] => {
  // Base columns (existing columns)
  const baseColumns = [
    {
      key: "name",
      title: "Campaign Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.XLARGE,
      required: true, // This is the primary column that cannot be hidden
      render: (campaign: Campaign) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <CampaignLink
              originalStorage="campaigns::list"
              nextStorage="campaigns::explorer"
              href={`/campaigns/${campaign.id}/explorer`}
              className="text-foreground p-4 rounded hover:bg-accent transition-colors block w-full h-full"
              title={campaign.name}
            >
              {campaign.name}
            </CampaignLink>
          </TooltipTrigger>
          <TooltipContent>
            <p>View in Campaign Explorer</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: "type",
      title: "Campaign Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 150, // Fixed
      filterOptions:
        filterOptions?.type?.map((type) => ({
          id: type,
          label: type,
        })) || [],
      render: (campaign: Campaign) => campaign.type || "-",
    },
    {
      key: "start_date",
      title: "Start Date",
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 120, // Fixed
      render: (campaign: Campaign) =>
        campaign.start_date ? formatDate(campaign.start_date) : "-",
      exportValue: (campaign: Campaign) =>
        campaign.start_date ? formatDate(campaign.start_date) : "-",
    },
    {
      key: "end_date",
      title: "End Date",
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 120, // Fixed
      render: (campaign: Campaign) =>
        campaign.end_date ? formatDate(campaign.end_date) : "-",
      exportValue: (campaign: Campaign) =>
        campaign.end_date ? formatDate(campaign.end_date) : "-",
    },
    {
      key: "description",
      title: "Description",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 200, // Fixed
      render: (campaign: Campaign) => campaign.description || "-",
    },
    {
      key: "source_record_type",
      title: "Source System",
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions:
        filterOptions?.source_record_type?.map((type) => ({
          id: type,
          label: type,
        })) || [],
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 140, // Fixed
      render: (campaign: Campaign) => {
        if (isLoadingConnections) {
          return (
            <div className="flex items-center justify-center w-8 h-8">
              <Skeleton className="h-4 w-4" />
            </div>
          );
        }
        const url = getExternalConnectionLink(campaign, connections);
        const systemName = getExternalSystemName(campaign.source_record_type);

        if (!campaign.source_record_type) {
          return <span className="text-muted-foreground">-</span>;
        }

        // If URL exists, render text, icon, link, and tooltip
        if (url) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <span>{campaign.source_record_type}</span>
                  <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                </a>
              </TooltipTrigger>
              <TooltipContent className="max-w-[20rem]">
                <p>View in {systemName}</p>
              </TooltipContent>
            </Tooltip>
          );
        } else {
          // If no URL, render only the literal text
          return <span>{campaign.source_record_type}</span>;
        }
      },
      exportable: false,
    },
    {
      key: "source_record_id",
      title: "Source Campaign ID",
      exportOnly: true,
    },
  ] as UnifiedTableColumn<Campaign>[];

  // Add metric columns if selectedMetrics is provided
  if (selectedMetrics && selectedMetrics.length > 0) {
    const metricColumns = selectedMetrics.map((metricKey) => {
      const metricConfig = METRIC_CONFIG[metricKey];
      const dataType = METRIC_DATA_TYPES[metricKey];

      // Map data types to filter types
      const getFilterType = (dataType: string): string => {
        switch (dataType) {
          case "integer":
          case "float":
          case "currency":
            return "number";
          case "date":
            return "date";
          default:
            return "text";
        }
      };

      return {
        key: `metric_${metricKey}`,
        title: metricConfig.title,
        tooltip: metricConfig.tooltip,
        columnType: "metric",
        sortable: true,
        filterable: true,
        filterType: getFilterType(dataType),
        nullSort: "last",
        defaultSortDirection: "desc",
        render: (
          campaign: Campaign & {
            metrics?: Record<
              keyof CampaignMetrics,
              number | string | undefined
            >;
          },
        ) => {
          if (!campaign.metrics) {
            return <Skeleton className="h-4 w-12" />;
          }

          const metricValue = campaign.metrics?.[metricKey];
          if (metricValue === undefined || metricValue === null) {
            return <span className="text-muted-foreground">-</span>;
          }

          // Use data type from METRIC_DATA_TYPES for rendering
          const dataType = METRIC_DATA_TYPES[metricKey];

          const formatValue = (
            value: number | string,
            type: string,
          ): string => {
            switch (type) {
              case "currency":
                return (value as number).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                });
              case "float":
                return (value as number).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                });
              case "date":
                return formatDate(value as string);
              default:
                return (value as number).toLocaleString("en-US");
            }
          };

          const formattedContent = (
            <span className="font-medium">
              {formatValue(metricValue, dataType)}
            </span>
          );

          // Check if this metric supports drill-down
          const supportsClickableDrilldown =
            metricConfig.drilldownColumns &&
            metricConfig.drilldownColumns.length > 0;

          const clickableContent =
            onMetricClick && supportsClickableDrilldown ? (
              <button
                className="w-full h-full text-left p-0 hover:underline decoration-2 underline-offset-4 transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMetricClick(campaign.id, metricKey);
                }}
              >
                {formattedContent}
              </button>
            ) : (
              formattedContent
            );

          return clickableContent;
        },
        exportValue: (campaign: Campaign | CampaignWithFormulaColumns) => {
          // For CSV export, check if metrics are flattened to top-level properties (CampaignWithMetrics)
          const flattenedMetricKey = `metric_${metricKey}`;
          const flattenedValue = (
            campaign as unknown as Record<string, unknown>
          )[flattenedMetricKey];

          if (flattenedValue !== undefined && flattenedValue !== null) {
            return String(flattenedValue);
          }

          // Fallback to nested metrics structure
          if (!campaign.metrics) {
            return "";
          }

          const metricValue = campaign.metrics?.[metricKey];
          if (metricValue === undefined || metricValue === null) {
            return "";
          }

          return String(metricValue);
        },
        category: metricConfig.category,
        categoryStyle: CATEGORY_STYLES[metricConfig.category]?.color,
      } as UnifiedTableColumn<Campaign>;
    });

    const columnsWithMetrics = [...baseColumns, ...metricColumns];

    // Combine formula columns and custom field columns
    const additionalColumns = [
      ...(formulaColumns || []),
      ...(customFields && customFields.length > 0
        ? createCustomFieldColumns(customFields)
        : []),
    ];

    return [...columnsWithMetrics, ...additionalColumns] as CampaignColumn[];
  }

  // When no metrics are selected, include formula columns and custom field columns
  const additionalColumns = [
    ...(formulaColumns || []),
    ...(customFields && customFields.length > 0
      ? createCustomFieldColumns(customFields)
      : []),
  ];

  return [...baseColumns, ...additionalColumns] as CampaignColumn[];
};

// Helper: convert UnifiedTableColumn list to TanStack ColumnDef list for useReactTable
// Note: This does a minimal mapping: header, cell renderer, size, id/accessorKey.
// Resize, sorting & filtering will be handled in campaigns-data-table.tsx via external logic.
export const getCampaignColumnDefs = (
  filterOptions?: { type?: string[]; source_record_type?: string[] },
  connections?: ExternalConnectionLinks[],
  isLoadingConnections?: boolean,
  selectedMetrics?: Array<keyof CampaignMetrics>,
  formulaColumns?: FormulaTableColumn[],
  customFields?: CustomFieldDefinition[],
  onMetricClick?: (campaignId: string, metric: keyof CampaignMetrics) => void,
): ColumnDef<Campaign>[] => {
  const unifiedColumns = getCampaignColumns(
    filterOptions,
    connections,
    isLoadingConnections,
    selectedMetrics,
    formulaColumns,
    customFields,
    onMetricClick,
  );

  // Exclude columns that are for export only
  return unifiedColumns
    .filter((col) => !col.exportOnly)
    .map((col): ColumnDef<Campaign> => {
      const colId = col.key.toString();

      // Basic cell renderer: use provided render or raw value
      const cellRenderer = col.render
        ? (info: { row: { original: Campaign } }) =>
            typeof col.render === "function"
              ? col.render(info.row.original)
              : "-"
        : (info: { getValue: () => unknown }) => {
            const val = info.getValue();
            return val === null || val === undefined || val === ""
              ? "-"
              : (val as React.ReactNode);
          };

      // Use column size
      const size = col.size;

      return {
        id: colId,
        accessorKey: typeof col.key === "string" ? col.key : undefined,
        header: () => col.title,
        cell: cellRenderer,
        enableSorting: !!col.sortable,
        size,
        meta: {
          title: col.title, // Store the column title in meta for accessibility
          categoryStyle: col.categoryStyle,
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          isCustomField: col.isCustomField,
          customFieldDataType: col.customFieldDataType,
          dataType: col.dataType, // Include simple data type string
          required: col.required, // Pass through the required flag
        },
      } as ColumnDef<Campaign>;
    });
};
